import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Globe, ArrowRight, Shield, Calendar, AlertTriangle, CheckCircle,
  Clock, FileText, Building2, ChevronRight, Search
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';

/* ─── Country Mandate Data (SEO-optimized) ─── */
const COUNTRY_PAGES = {
  'italy': {
    code: 'IT', name: 'Italy', region: 'Europe',
    headline: 'Italy e-Invoicing Mandate: Complete Compliance Guide',
    subheadline: 'Everything you need to know about the Sistema di Interscambio (SDI) and B2B electronic invoicing in Italy.',
    status: 'live', statusLabel: 'In Force Since 2019', statusColor: '#10b981',
    mandateType: 'e-Invoicing (SDI Clearance)',
    effectiveDate: 'January 1, 2019',
    overview: 'Italy pioneered mandatory B2B electronic invoicing in the EU, requiring all domestic invoices to pass through the government\'s clearance platform, the Sistema di Interscambio (SDI). This Continuous Transaction Control (CTC) model has become the blueprint for other EU member states.',
    keyRequirements: [
      'All B2B and B2C invoices must be transmitted through SDI in XML format (FatturaPA)',
      'Invoices must be validated by SDI before delivery to the buyer',
      'Cross-border invoices to non-Italian entities reported via "Esterometro" (now integrated into SDI)',
      'Invoice storage must comply with Italian digital archiving regulations (conservazione sostitutiva)',
      'Real-time transmission required — paper invoices are no longer valid for tax purposes',
    ],
    penalties: [
      'Fines of 90%–180% of the VAT on unreported invoices',
      'Late submission: €250–€2,000 per invoice',
      'Missing invoices in SDI: full VAT disallowance on input tax deduction',
    ],
    timeline: [
      { date: 'Jan 2019', label: 'Mandatory for all B2B/B2C domestic invoices' },
      { date: 'Jul 2022', label: 'Cross-border invoices via SDI (Esterometro abolished)' },
      { date: '2024', label: 'Flat-rate taxpayers included in the mandate' },
    ],
    taxeraValue: 'Taxera provides full SDI integration with automated FatturaPA XML generation, real-time transmission, and digital archiving compliance.',
  },
  'france': {
    code: 'FR', name: 'France', region: 'Europe',
    headline: 'France e-Invoicing & eReporting: Mandate Timeline & Requirements',
    subheadline: 'Navigate the French Chorus Pro expansion and upcoming B2B e-Invoicing mandate with confidence.',
    status: 'rollout', statusLabel: 'Rollout Starting Sep 2026', statusColor: '#f59e0b',
    mandateType: 'e-Invoicing & eReporting (PDP Model)',
    effectiveDate: 'September 1, 2026 (Large enterprises)',
    overview: 'France is implementing a phased B2B e-Invoicing and eReporting mandate through certified Plateforme de Dématérialisation Partenaire (PDP) intermediaries. Large enterprises must comply first, with all businesses included by 2028.',
    keyRequirements: [
      'All B2B invoices must flow through registered PDP platforms or the public Chorus Pro portal',
      'Invoices in Factur-X (PDF/A-3 with XML), UBL, or CII formats',
      'eReporting required for B2C transactions and cross-border operations',
      'Lifecycle management: invoice statuses (received, rejected, paid) must be reported',
      'PDP platforms must be registered with the French tax authority (DGFiP)',
    ],
    penalties: [
      '€15 per invoice for non-compliant format (capped at €15,000 per year)',
      'Additional penalties for failure to report eReporting data',
      'Potential VAT audit triggers for systematic non-compliance',
    ],
    timeline: [
      { date: 'Sep 2026', label: 'Large enterprises: mandatory issuance & reception' },
      { date: 'Sep 2027', label: 'Mid-sized enterprises (ETI): mandatory issuance' },
      { date: 'Sep 2028', label: 'SMEs and micro-enterprises: full mandate' },
    ],
    taxeraValue: 'Taxera is PDP-ready with native Factur-X generation, UBL/CII support, and automated eReporting submission.',
  },
  'germany': {
    code: 'DE', name: 'Germany', region: 'Europe',
    headline: 'Germany B2B e-Invoicing Mandate: What to Expect in 2027',
    subheadline: 'Germany is preparing for mandatory B2B electronic invoicing. Here\'s what businesses need to know.',
    status: 'upcoming', statusLabel: 'Mandatory from January 2027', statusColor: '#8b5cf6',
    mandateType: 'B2B e-Invoicing (EN 16931)',
    effectiveDate: 'January 1, 2027 (mandatory issuance for large enterprises)',
    overview: 'Germany\'s Growth Opportunities Act (Wachstumschancengesetz) mandates B2B e-Invoicing from 2027. All domestic B2B invoices must be in structured electronic format compliant with EU standard EN 16931.',
    keyRequirements: [
      'All B2B invoices must be in structured electronic format (XRechnung or ZUGFeRD 2.x)',
      'Paper and PDF-only invoices will no longer be accepted for B2B transactions',
      'From Jan 2025: all businesses must be able to RECEIVE e-Invoices',
      'From Jan 2027: businesses with >€800k revenue must ISSUE e-Invoices',
      'From Jan 2028: all businesses must issue e-Invoices',
    ],
    penalties: [
      'Non-compliant invoices will not be recognized for VAT deduction purposes',
      'General tax penalty framework applies (up to €25,000 per violation)',
      'Increased audit probability for businesses not complying with format requirements',
    ],
    timeline: [
      { date: 'Jan 2025', label: 'All businesses must accept e-Invoices' },
      { date: 'Jan 2027', label: 'Large enterprises must issue e-Invoices' },
      { date: 'Jan 2028', label: 'All businesses must issue e-Invoices' },
    ],
    taxeraValue: 'Taxera generates EN 16931-compliant XRechnung and ZUGFeRD formats natively from SAP, ensuring seamless transition.',
  },
  'poland': {
    code: 'PL', name: 'Poland', region: 'Europe',
    headline: 'Poland KSeF e-Invoicing: National eInvoice System Guide',
    subheadline: 'Poland\'s KSeF platform makes e-Invoicing mandatory for all businesses from February 2026.',
    status: 'live', statusLabel: 'Mandatory Feb 2026', statusColor: '#10b981',
    mandateType: 'KSeF (Krajowy System e-Faktur)',
    effectiveDate: 'February 2026',
    overview: 'Poland\'s Krajowy System e-Faktur (KSeF) is the national platform for issuing, receiving, and storing structured electronic invoices. After a delay from the original July 2024 date, the mandate becomes effective in February 2026.',
    keyRequirements: [
      'All invoices must be issued through the KSeF platform in structured XML format',
      'Invoices receive a unique KSeF identification number upon acceptance',
      'Real-time validation by the tax authority before delivery',
      'B2C invoices are exempt but can optionally use KSeF',
      'Integration via API or authorized billing software required',
    ],
    penalties: [
      'Fines of up to 100% of the VAT amount on invoices not issued through KSeF',
      'Administrative penalties for repeated non-compliance',
      'Invoices outside KSeF may not be recognized for VAT deduction',
    ],
    timeline: [
      { date: 'Jan 2022', label: 'KSeF launched as voluntary system' },
      { date: 'Feb 2026', label: 'Mandatory for all VAT-registered businesses' },
    ],
    taxeraValue: 'Taxera provides direct KSeF API integration with automated XML generation and real-time submission from SAP.',
  },
  'spain': {
    code: 'ES', name: 'Spain', region: 'Europe',
    headline: 'Spain Veri*factu & SII: Real-Time Reporting Requirements',
    subheadline: 'Spain requires real-time invoice reporting through SII and the upcoming Veri*factu system.',
    status: 'live', statusLabel: 'SII In Force / Veri*factu 2026', statusColor: '#10b981',
    mandateType: 'SII + Veri*factu Real-Time Reporting',
    effectiveDate: 'SII: July 2017 / Veri*factu: 2026',
    overview: 'Spain operates the SII (Suministro Inmediato de Información) real-time reporting system for large taxpayers, and is expanding with Veri*factu — a system requiring certified invoicing software to generate verifiable records.',
    keyRequirements: [
      'SII: Large companies (>€6M revenue) must report invoices within 4 days',
      'Veri*factu: All invoicing software must generate signed, tamper-proof records',
      'QR codes on invoices linking to tax authority verification',
      'Anti-fraud measures embedded in billing software certification',
    ],
    penalties: [
      'SII late reporting: €1–€6,000 per quarter',
      'Veri*factu non-compliance: up to €50,000 per tax year',
      'Using uncertified software: additional penalties',
    ],
    timeline: [
      { date: 'Jul 2017', label: 'SII mandatory for large taxpayers' },
      { date: '2026', label: 'Veri*factu certification required for all software' },
    ],
    taxeraValue: 'Taxera supports both SII real-time reporting and Veri*factu compliance with certified invoice generation.',
  },
  'saudi-arabia': {
    code: 'SA', name: 'Saudi Arabia', region: 'Middle East',
    headline: 'Saudi Arabia ZATCA e-Invoicing (FATOORA): Phase 2 Guide',
    subheadline: 'Saudi Arabia\'s ZATCA e-Invoicing mandate is in Phase 2 integration with the FATOORA platform.',
    status: 'live', statusLabel: 'Phase 2 Active', statusColor: '#10b981',
    mandateType: 'ZATCA e-Invoicing (FATOORA)',
    effectiveDate: 'Phase 1: Dec 2021 / Phase 2: Rolling waves from Jan 2023',
    overview: 'Saudi Arabia\'s ZATCA (Zakat, Tax and Customs Authority) mandates electronic invoicing in two phases. Phase 1 (Generation) requires electronic invoice generation. Phase 2 (Integration) requires real-time reporting to the FATOORA platform.',
    keyRequirements: [
      'All taxable persons must generate invoices electronically (Phase 1)',
      'Phase 2: Real-time clearance through FATOORA for B2B invoices',
      'Invoices must include QR codes with TLV-encoded data',
      'XML format compliant with ZATCA specifications (based on UBL 2.1)',
      'Cryptographic stamping (digital signature) required in Phase 2',
    ],
    penalties: [
      'Phase 1 non-compliance: SAR 5,000 per violation (first offense)',
      'Repeated violations: up to SAR 50,000',
      'Phase 2: potential suspension of business activities',
    ],
    timeline: [
      { date: 'Dec 2021', label: 'Phase 1: Electronic generation mandatory' },
      { date: 'Jan 2023', label: 'Phase 2: First wave of taxpayers (>SAR 3B revenue)' },
      { date: '2024–2025', label: 'Phase 2: Additional waves for smaller taxpayers' },
    ],
    taxeraValue: 'Taxera supports ZATCA Phase 2 with automated UBL 2.1 generation, cryptographic stamping, and FATOORA API integration.',
  },
  'brazil': {
    code: 'BR', name: 'Brazil', region: 'Latin America',
    headline: 'Brazil NF-e & CT-e: Latin America\'s e-Invoicing Pioneer',
    subheadline: 'Brazil\'s NF-e system has been mandatory since 2008, making it one of the most mature CTC models globally.',
    status: 'live', statusLabel: 'In Force Since 2008', statusColor: '#10b981',
    mandateType: 'NF-e / NFS-e / CT-e (Clearance Model)',
    effectiveDate: '2008 (rolling)',
    overview: 'Brazil pioneered Continuous Transaction Controls (CTC) with its Nota Fiscal Eletrônica (NF-e) system. All commercial transactions require pre-authorization from SEFAZ before the goods can be shipped.',
    keyRequirements: [
      'NF-e (goods): XML invoice must be authorized by SEFAZ before shipment',
      'NFS-e (services): Municipal-level electronic service invoices',
      'CT-e (transport): Electronic transport documents',
      'Real-time validation and authorization required',
      'DANFE (document) must accompany physical goods',
    ],
    penalties: [
      'Goods seizure if shipped without authorized NF-e',
      'Fines ranging from 1%–100% of the transaction value',
      'Criminal liability for systematic evasion',
    ],
    timeline: [
      { date: '2008', label: 'NF-e mandatory for large taxpayers' },
      { date: '2010', label: 'NF-e extended to all sectors' },
      { date: '2023', label: 'NFS-e national standardization (ongoing)' },
    ],
    taxeraValue: 'Taxera integrates with SEFAZ for NF-e authorization, NFS-e municipal submission, and CT-e transport documentation.',
  },
  'india': {
    code: 'IN', name: 'India', region: 'Asia Pacific',
    headline: 'India e-Invoicing Under GST: IRP Portal Compliance Guide',
    subheadline: 'India mandates e-Invoicing for businesses above the GST turnover threshold through the IRP.',
    status: 'live', statusLabel: 'In Force (Phased)', statusColor: '#10b981',
    mandateType: 'GST e-Invoicing (IRP Clearance)',
    effectiveDate: 'October 2020 (large; expanding to smaller businesses)',
    overview: 'India\'s GST e-Invoicing system requires businesses above specified turnover thresholds to register invoices on the Invoice Registration Portal (IRP) and obtain an Invoice Reference Number (IRN).',
    keyRequirements: [
      'Invoices must be registered with IRP in JSON format to receive an IRN',
      'QR code with digitally signed invoice data must appear on the invoice',
      'Currently mandatory for businesses with turnover >₹5 crore',
      'E-way bills integrated with the e-Invoicing system',
    ],
    penalties: [
      '100% of tax amount or ₹10,000 (whichever is higher) for non-registration',
      'Input tax credit denial for non-compliant invoices',
      'E-way bill generation blocked without valid eInvoice',
    ],
    timeline: [
      { date: 'Oct 2020', label: 'Mandatory for turnover >₹500 crore' },
      { date: 'Aug 2023', label: 'Extended to turnover >₹5 crore' },
      { date: 'TBD', label: 'Expected extension to all GST-registered businesses' },
    ],
    taxeraValue: 'Taxera automates IRP registration, IRN generation, and QR code embedding from SAP with real-time compliance checks.',
  },
};

const ALL_COUNTRY_SLUGS = Object.keys(COUNTRY_PAGES);

/* ─── Derive filter options from data ─── */
const ALL_REGIONS = [...new Set(Object.values(COUNTRY_PAGES).map(p => p.region))].sort();
const ALL_STATUSES = [...new Set(Object.values(COUNTRY_PAGES).map(p => p.status))];
const STATUS_META = { live: { label: 'In Force', color: '#10b981' }, rollout: { label: 'Rolling Out', color: '#f59e0b' }, upcoming: { label: 'Upcoming', color: '#8b5cf6' } };

/* ─── SEO Index Page ─── */
const SEOIndexPage = () => {
  const [regionFilter, setRegionFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    return ALL_COUNTRY_SLUGS.filter((slug) => {
      const page = COUNTRY_PAGES[slug];
      if (regionFilter && page.region !== regionFilter) return false;
      if (statusFilter && page.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!page.name.toLowerCase().includes(q) && !page.mandateType.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [regionFilter, statusFilter, search]);

  const hasFilters = regionFilter || statusFilter || search;

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto px-6 py-16" data-testid="seo-index-page">
        <div className="text-center mb-12">
          <span className="text-xs font-bold text-taxera-teal uppercase tracking-widest">Knowledge Base</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-taxera-charcoal mt-3 mb-4">
            Country Compliance Guides
          </h1>
          <p className="text-base text-slate-500 max-w-2xl mx-auto">
            In-depth guides for e-Invoicing and tax compliance mandates across the globe. Stay informed about requirements, timelines, and penalties.
          </p>
        </div>

        {/* Filters */}
        <div className="mb-8 space-y-3" data-testid="country-guide-filters">
          <div className="flex flex-wrap items-center gap-3">
            {/* Region filter */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-2xs font-bold text-slate-400 uppercase tracking-wide shrink-0">Region:</span>
              {ALL_REGIONS.map(r => (
                <button key={r} onClick={() => setRegionFilter(prev => prev === r ? null : r)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${regionFilter === r ? 'bg-taxera-blue text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  data-testid={`guide-region-${r.toLowerCase().replace(/\s+/g, '-')}`}>
                  {r}
                </button>
              ))}
            </div>
            {/* Status filter */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-2xs font-bold text-slate-400 uppercase tracking-wide shrink-0">Status:</span>
              {ALL_STATUSES.map(s => {
                const meta = STATUS_META[s] || { label: s, color: '#94a3b8' };
                return (
                  <button key={s} onClick={() => setStatusFilter(prev => prev === s ? null : s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${statusFilter === s ? 'text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'}`}
                    style={statusFilter === s ? { backgroundColor: meta.color } : {}}
                    data-testid={`guide-status-${s}`}>
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Search */}
          <div className="flex items-center gap-3">
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search countries or mandate types..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-taxera-teal focus:ring-1 focus:ring-taxera-teal/20"
                data-testid="guide-search" />
            </div>
            {hasFilters && (
              <button onClick={() => { setRegionFilter(null); setStatusFilter(null); setSearch(''); }}
                className="text-xs font-semibold text-taxera-teal hover:underline" data-testid="guide-clear-filters">
                Clear filters
              </button>
            )}
            <span className="text-xs text-slate-400 ml-auto">{filtered.length} guide{filtered.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((slug) => {
          const page = COUNTRY_PAGES[slug];
          return (
            <Link key={slug} to={`/knowledge-base/${slug}`}
              className="group bg-white rounded-2xl border border-slate-200 p-5 hover:border-taxera-teal hover:shadow-lg transition-all"
              data-testid={`seo-card-${slug}`}>
              <div className="flex items-center gap-3 mb-3">
                <img src={`https://flagcdn.com/24x18/${page.code.toLowerCase()}.png`} alt={page.name} className="w-6 h-4.5 rounded-sm border border-slate-100" />
                <span className="text-sm font-bold text-taxera-charcoal group-hover:text-taxera-blue transition-colors">{page.name}</span>
              </div>
              <span className="text-2xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: `${page.statusColor}15`, color: page.statusColor }}>
                {page.statusLabel}
              </span>
              <p className="text-xs text-slate-400 mt-2 line-clamp-2">{page.mandateType}</p>
              <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-taxera-teal group-hover:gap-2 transition-all">
                Read guide <ChevronRight className="w-3 h-3" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  </PageLayout>
  );
};

/* ─── Individual Country SEO Page ─── */
const SEOCountryPage = () => {
  const { country } = useParams();
  const page = COUNTRY_PAGES[country];

  if (!page) {
    return (
      <PageLayout>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <Globe className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-taxera-charcoal mb-2">Country Not Found</h2>
          <p className="text-sm text-slate-500 mb-4">We don't have a compliance guide for this country yet.</p>
          <Link to="/knowledge-base" className="text-sm font-semibold text-taxera-blue hover:underline">Browse all guides</Link>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto px-6 py-16" data-testid={`seo-page-${country}`}>
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-xs text-slate-400 mb-8">
          <Link to="/knowledge-base" className="hover:text-taxera-blue transition-colors">Knowledge Base</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-taxera-charcoal font-medium">{page.name}</span>
        </nav>

        {/* Hero */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <img src={`https://flagcdn.com/40x30/${page.code.toLowerCase()}.png`} alt={page.name} className="w-10 h-7.5 rounded border border-slate-100" />
            <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ backgroundColor: `${page.statusColor}15`, color: page.statusColor }}>
              {page.statusLabel}
            </span>
            <span className="text-xs text-slate-400">{page.region}</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-taxera-charcoal mb-3" data-testid="seo-page-headline">
            {page.headline}
          </h1>
          <p className="text-base text-slate-500">{page.subheadline}</p>
        </div>

        {/* Quick Facts */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <Shield className="w-5 h-5 text-taxera-blue mb-2" />
            <div className="text-2xs font-bold text-slate-400 uppercase tracking-wide mb-1">Mandate Type</div>
            <div className="text-sm font-semibold text-taxera-charcoal">{page.mandateType}</div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <Calendar className="w-5 h-5 text-taxera-teal mb-2" />
            <div className="text-2xs font-bold text-slate-400 uppercase tracking-wide mb-1">Effective Date</div>
            <div className="text-sm font-semibold text-taxera-charcoal">{page.effectiveDate}</div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <Globe className="w-5 h-5 text-violet-500 mb-2" />
            <div className="text-2xs font-bold text-slate-400 uppercase tracking-wide mb-1">Region</div>
            <div className="text-sm font-semibold text-taxera-charcoal">{page.region}</div>
          </div>
        </div>

        {/* Overview */}
        <section className="mb-10">
          <h2 className="font-heading text-xl font-bold text-taxera-charcoal mb-3">Overview</h2>
          <p className="text-sm text-slate-600 leading-relaxed">{page.overview}</p>
        </section>

        {/* Key Requirements */}
        <section className="mb-10">
          <h2 className="font-heading text-xl font-bold text-taxera-charcoal mb-4">Key Requirements</h2>
          <div className="space-y-2">
            {page.keyRequirements.map((req, i) => (
              <div key={`req-${req.slice(0, 30)}`} className="flex items-start gap-3 bg-white rounded-xl border border-slate-200 p-4" data-testid={`seo-req-${i}`}>
                <CheckCircle className="w-4 h-4 text-taxera-teal shrink-0 mt-0.5" />
                <p className="text-sm text-slate-600">{req}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Timeline */}
        <section className="mb-10">
          <h2 className="font-heading text-xl font-bold text-taxera-charcoal mb-4">Timeline</h2>
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-200" />
            <div className="space-y-4">
              {page.timeline.map((t, i) => (
                <div key={`timeline-${t.date}`} className="flex items-start gap-4 relative" data-testid={`seo-timeline-${i}`}>
                  <div className="w-8 h-8 rounded-full bg-taxera-blue/10 flex items-center justify-center shrink-0 z-10">
                    <Clock className="w-3.5 h-3.5 text-taxera-blue" />
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4 flex-1">
                    <div className="text-xs font-bold text-taxera-blue mb-1">{t.date}</div>
                    <p className="text-sm text-slate-600">{t.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Penalties */}
        <section className="mb-10">
          <h2 className="font-heading text-xl font-bold text-taxera-charcoal mb-4">Non-Compliance Penalties</h2>
          <div className="bg-red-50/50 rounded-2xl border border-red-100 p-5">
            <div className="space-y-2">
              {page.penalties.map((p, i) => (
                <div key={`penalty-${p.slice(0, 30)}`} className="flex items-start gap-2" data-testid={`seo-penalty-${i}`}>
                  <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{p}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="rounded-2xl bg-gradient-to-r from-taxera-blue/5 to-taxera-teal/5 border border-taxera-blue/10 p-8 text-center">
          <Building2 className="w-8 h-8 text-taxera-blue mx-auto mb-3" />
          <h3 className="font-heading text-lg font-bold text-taxera-charcoal mb-2">
            How Taxera Helps with {page.name} Compliance
          </h3>
          <p className="text-sm text-slate-500 max-w-lg mx-auto mb-5">{page.taxeraValue}</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/evaluate"
              className="inline-flex items-center gap-2 px-6 py-3 taxera-gradient text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all"
              data-testid="seo-cta-assess">
              Start Assessment <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-taxera-charcoal border border-slate-200 rounded-full text-sm font-semibold hover:border-slate-300 transition-all"
              data-testid="seo-cta-contact">
              Talk to Us
            </Link>
          </div>
        </section>

        {/* Related Pages */}
        <div className="mt-12">
          <h3 className="text-sm font-bold text-taxera-charcoal mb-4">Other Country Guides</h3>
          <div className="flex flex-wrap gap-2">
            {ALL_COUNTRY_SLUGS.filter(s => s !== country).map(slug => {
              const p = COUNTRY_PAGES[slug];
              return (
                <Link key={slug} to={`/knowledge-base/${slug}`}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-600 hover:border-taxera-teal hover:text-taxera-blue transition-all"
                  data-testid={`seo-related-${slug}`}>
                  <img src={`https://flagcdn.com/16x12/${p.code.toLowerCase()}.png`} alt={p.name} className="w-4 h-3 rounded-sm" />
                  {p.name}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export { SEOIndexPage, SEOCountryPage, COUNTRY_PAGES };
